import { z } from "zod";
import { startMsSchema } from "./common";

const nameSchema = z.string().trim().min(1).max(80);

const numberSchema = z.number().int().min(0).max(999);

const songLabelSchema = z.string().trim().max(120);

/**
 * Region length as sent. The 3-30 s rule is applied by validateRegion so that an out-of-range
 * length gets the stable code region_invalid, not a generic validation error.
 */
const lengthInputSchema = z.number().finite();

/** Operator-only fields: names, numbers, photo and song lock, and media and region attachment. */
export const personCreateSchema = z.object({
  name: nameSchema,
  number: numberSchema.nullable().optional(),
});

export const personOperatorUpdateSchema = z
  .object({
    name: nameSchema.optional(),
    number: numberSchema.nullable().optional(),
    photo_locked: z.boolean().optional(),
    song_locked: z.boolean().optional(),
    photo_media_id: z.uuid().nullable().optional(),
    song_media_id: z.uuid().nullable().optional(),
    song_label: songLabelSchema.nullable().optional(),
    start_ms: startMsSchema.optional(),
    length_s: lengthInputSchema.optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, { message: "Nothing to update" });

/**
 * Player-editable fields only. Any other key, including name and number, is rejected
 * with a validation error rather than silently dropped.
 */
export const personPlayerPatchSchema = z
  .object({
    song_media_id: z.uuid().optional(),
    start_ms: startMsSchema.optional(),
    length_s: lengthInputSchema.optional(),
    photo_media_id: z.uuid().optional(),
    consent: z.boolean().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, { message: "Nothing to update" });

export type PersonCreate = z.infer<typeof personCreateSchema>;
export type PersonOperatorUpdate = z.infer<typeof personOperatorUpdateSchema>;
export type PersonPlayerPatch = z.infer<typeof personPlayerPatchSchema>;

/** Operator-only: one-step invite. Creates the person and a player link together. */
export const personInviteSchema = z
  .object({
    name: nameSchema,
    number: numberSchema.nullable().optional(),
  })
  .strict();

/** Operator view of a player link. `url` is null when it cannot be read back. */
export const personLinkSchema = z.object({
  url: z.string().nullable(),
  generatedAt: z.string().nullable(),
  openedAt: z.string().nullable(),
  unavailable: z.boolean(),
});

/** Operator response for a person row (list, get, create, update, invite). */
export const personResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  number: z.number().int().nullable(),
  photoMediaId: z.string().nullable(),
  songMediaId: z.string().nullable(),
  songLabel: z.string().nullable(),
  startMs: z.number(),
  lengthS: z.number(),
  photoLocked: z.boolean(),
  songLocked: z.boolean(),
  consentAt: z.string().nullable(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  songChosen: z.boolean(),
  link: personLinkSchema.nullable(),
});

export type PersonInvite = z.infer<typeof personInviteSchema>;
export type PersonLink = z.infer<typeof personLinkSchema>;
export type PersonResponse = z.infer<typeof personResponseSchema>;
